import { type HistoryEntry, usePageEditorStore } from 'app/store/editor';
import { useShallow } from 'zustand/react/shallow';

export const useHistory = () => {
    const editorStore = usePageEditorStore(
        useShallow((api) => ({
            setRootSections: api.setRootSections,
            setFlatMap: api.setFlatMap,
            setStyleMap: api.setStyleMap,
            setContentMap: api.setContentMap,
            setSiteStyle: api.setSiteStyle,
            getHistoryStack: api.getHistoryStack,
            getFutureStack: api.getFutureStack,
            setHistoryStack: api.setHistoryStack,
            setFutureStack: api.setFutureStack,
            record: api.record,
        }))
    );

    function batchAddNodeInverse(
        entry: Extract<HistoryEntry, { type: 'batch+add' | 'batch+remove' }>
    ) {
        const {
            parentId,
            targetIndex,
            targetSectionName,
            nodesUpdated,
            nodesStyleUpdated,
            nodesContentUpdated,
        } = entry;

        editorStore.setStyleMap((prev) => {
            const _prev = { ...prev };

            const deleteKeys = Object.keys(nodesStyleUpdated);

            deleteKeys.forEach((key) => {
                delete _prev[key];
            });

            return _prev;
        });

        editorStore.setContentMap((prev) => {
            const _prev = { ...prev };

            const deleteKeys = Object.keys(nodesContentUpdated);

            deleteKeys.forEach((key) => {
                delete _prev[key];
            });

            return _prev;
        });

        editorStore.setFlatMap((prev) => {
            const _prev = { ...prev };

            const deleteKeys = Object.keys(nodesUpdated!);

            if (parentId) {
                _prev[parentId].next.splice(targetIndex, 1);
            }

            deleteKeys.forEach((key) => {
                delete _prev[key];
            });

            return _prev;
        });

        if (!parentId) {
            editorStore.setRootSections((prev) => {
                const _prev = { ...prev };

                _prev[targetSectionName].splice(targetIndex, 1);

                return _prev;
            });
        }
    }

    function batchAddNodeEntry(
        entry: Extract<HistoryEntry, { type: 'batch+add' | 'batch+remove' }>
    ) {
        const {
            parentId,
            targetIndex,
            targetNodeId,
            targetSectionName,
            nodesUpdated,
            nodesStyleUpdated,
            nodesContentUpdated,
        } = entry;

        editorStore.setStyleMap((prev) => ({
            ...prev,
            ...nodesStyleUpdated,
        }));

        editorStore.setContentMap((prev) => ({
            ...prev,
            ...nodesContentUpdated,
        }));

        editorStore.setFlatMap((prev) => {
            const _prev = { ...prev };

            if (parentId) {
                _prev[parentId].next.splice(targetIndex, 0, targetNodeId);
            }

            return {
                ..._prev,
                ...nodesUpdated,
            };
        });

        if (!parentId) {
            editorStore.setRootSections((prev) => {
                const _prev = { ...prev };

                _prev[targetSectionName].splice(targetIndex, 0, targetNodeId);

                return _prev;
            });
        }
    }

    function batchMoveNodeEntry(
        entry: Extract<HistoryEntry, { type: 'batch+move' }>['next']
    ) {
        const { flatMap, rootSections, styleMap, contentMap } = entry;

        editorStore.setFlatMap((prev) => ({
            ...prev,
            ...flatMap,
        }));

        editorStore.setRootSections((prev) => ({
            ...prev,
            ...rootSections,
        }));

        editorStore.setStyleMap((prev) => ({
            ...prev,
            ...styleMap,
        }));

        editorStore.setContentMap((prev) => ({
            ...prev,
            ...contentMap,
        }));
    }

    function batchMoveNodeInverse(
        entry: Extract<HistoryEntry, { type: 'batch+move' }>['prev']
    ) {
        const { flatMap, rootSections, styleMap, contentMap } = entry;

        editorStore.setFlatMap(flatMap);

        editorStore.setRootSections(rootSections);

        editorStore.setStyleMap(styleMap);

        editorStore.setContentMap(contentMap);
    }

    function applyEntry(entry: HistoryEntry) {
        switch (entry.type) {
            case 'content':
                editorStore.setContentMap((prev) => ({
                    ...prev,
                    [entry.id]: entry.next,
                }));

                break;

            case 'style':
                editorStore.setStyleMap((prev) => ({
                    ...prev,
                    [entry.id]: entry.next,
                }));

                break;

            case 'siteStyle':
                editorStore.setSiteStyle(entry.next);

                break;

            case 'group':
                entry.entries.forEach(applyEntry);

                break;

            case 'root':
                editorStore.setRootSections(entry.next);

                break;

            case 'batch+add':
                batchAddNodeEntry(entry);

                break;

            case 'batch+remove':
                batchAddNodeInverse(entry);

                break;

            case 'batch+move':
                batchMoveNodeEntry(entry.next);

                break;

            case 'move':
                editorStore.setFlatMap((prev) => ({
                    ...prev,
                    ...entry.next,
                }));

                break;
        }
    }

    function applyInverse(entry: HistoryEntry) {
        switch (entry.type) {
            case 'content':
                editorStore.setContentMap((prev) => ({
                    ...prev,
                    [entry.id]: entry.prev,
                }));

                break;

            case 'style':
                editorStore.setStyleMap((prev) => ({
                    ...prev,
                    [entry.id]: entry.prev,
                }));

                break;

            case 'siteStyle':
                editorStore.setSiteStyle(entry.prev);

                break;

            case 'group':
                entry.entries.forEach(applyInverse);
                break;

            case 'root':
                editorStore.setRootSections(entry.prev);

                break;

            case 'batch+add':
                batchAddNodeInverse(entry);

                break;

            case 'batch+remove':
                batchAddNodeEntry(entry);

                break;

            case 'batch+move':
                batchMoveNodeInverse(entry.prev);

                break;

            case 'move':
                editorStore.setFlatMap(entry.prev);

                break;
        }
    }

    function undo() {
        const historyStack = editorStore.getHistoryStack();

        const entry = historyStack.pop();

        if (!entry) {
            return null;
        }

        applyInverse(entry);

        editorStore.setFutureStack((prev) => [...prev, entry]);

        return entry;
    }

    function redo() {
        const futureStack = editorStore.getFutureStack();

        const entry = futureStack.pop();

        if (!entry) {
            return null;
        }

        applyEntry(entry);

        editorStore.setHistoryStack((prev) => [...prev, entry]);

        return entry;
    }

    return {
        record: editorStore.record,
        getHistoryStack: editorStore.getHistoryStack,
        getFutureStack: editorStore.getFutureStack,
        undo,
        redo,
    };
};
